import React, { useState, useEffect } from 'react';
import axios from 'axios';
import SharedQuestionModule from './SharedQuestionModule';

const SharedQuestionsForOneCourse = ({ className, sharerName, onBack }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modules, setModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState(null);

  const apiUrl = process.env.REACT_APP_API_BASE_URL;

  // Fetch shared questions for this specific course
  useEffect(() => {
    const fetchCourseQuestions = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${apiUrl}/api/sharing/library`);
        
        // Filter questions for this specific course
        const courseQuestions = response.data.filter(
          question => question.className === className && question.sharerName === sharerName
        );
        
        // Group by question module and sort by dataIndex
        const moduleMap = courseQuestions.reduce((acc, question) => {
          const moduleKey = question.questionModule;
          if (!acc[moduleKey]) {
            acc[moduleKey] = {
              questionModule: moduleKey,
              className: question.className,
              sharerName: question.sharerName,
              sharedAt: question.sharedAt,
              questions: []
            };
          }
          acc[moduleKey].questions.push(question);
          return acc;
        }, {});
        
        // Convert to array and sort each module's questions by dataIndex
        const modulesArray = Object.values(moduleMap).map(module => ({
          ...module,
          questions: module.questions.sort((a, b) => {
            const aIndex = parseInt(a.dataIndex) || 0;
            const bIndex = parseInt(b.dataIndex) || 0;
            return aIndex - bIndex;
          })
        }));
        
        // Sort modules by the minimum dataIndex of their questions
        modulesArray.sort((a, b) => {
          const aMinIndex = Math.min(...a.questions.map(q => parseInt(q.dataIndex) || 0));
          const bMinIndex = Math.min(...b.questions.map(q => parseInt(q.dataIndex) || 0));
          return aMinIndex - bMinIndex;
        });
        
        setModules(modulesArray);
      } catch (err) {
        console.error('Error fetching course questions:', err);
        setError('Failed to load course questions');
      } finally {
        setLoading(false);
      }
    };

    fetchCourseQuestions();
  }, [apiUrl, className, sharerName]);

  // Handle module selection
  const handleModuleSelect = (module) => {
    setSelectedModule(module);
  };

  // Handle back to module list
  const handleBackToModules = () => {
    setSelectedModule(null);
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '400px',
        color: 'var(--text-color-muted)'
      }}>
        Loading course questions...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '60px 20px',
        color: '#dc2626',
        backgroundColor: '#fef2f2',
        borderRadius: '8px',
        border: '2px dashed #fecaca'
      }}>
        <div style={{ fontSize: '48px', marginBottom: '20px' }}>⚠️</div>
        <h3 style={{ marginBottom: '10px', color: '#dc2626' }}>Error Loading Course Questions</h3>
        <p>{error}</p>
      </div>
    );
  }

  // If a module is selected, show the SharedQuestionModule component
  if (selectedModule) {
    return (
      <SharedQuestionModule
        module={selectedModule}
        onBack={handleBackToModules}
      />
    );
  }

  // Show list of modules for this course
  return (
    <div style={{
      padding: '20px',
      fontFamily: 'var(--font-primary)',
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: 'var(--standard-box-shadow)'
    }}>
      {/* Header with back button */}
      <div style={{
        marginBottom: '24px'
      }}>
        <button
          onClick={onBack}
          style={{
            padding: '8px 16px',
            backgroundColor: 'var(--color2)',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            marginBottom: '16px'
          }}
        >
          ← Back to Course List
        </button>
        
        <div style={{ textAlign: 'center' }}>
          <h2 style={{
            margin: '0 0 8px 0',
            fontSize: '24px',
            fontWeight: '600',
            color: 'var(--color1)'
          }}>
            {className}
          </h2>
          <p style={{
            margin: 0,
            color: 'var(--text-color-muted)',
            fontSize: '14px'
          }}>
            Shared by {sharerName} • {modules.length} module{modules.length !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {modules.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          color: '#666',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          border: '2px dashed #dee2e6'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>📝</div>
          <h3 style={{ marginBottom: '10px', color: '#495057' }}>No Modules Found</h3>
          <p>This course doesn't have any shared question modules.</p>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gap: '12px' // Reduced from 16px
        }}>
          {modules.map((module, index) => (
            <div
              key={module.questionModule}
              onClick={() => handleModuleSelect(module)}
              style={{
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                backgroundColor: 'white',
                padding: '12px 16px', // Reduced from 20px
                cursor: 'pointer',
                boxShadow: 'var(--standard-box-shadow)', // Changed to CSS variable
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color1-light)';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = 'var(--standard-box-shadow)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'white';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'var(--standard-box-shadow)'; // Changed to use CSS variable
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <h4 style={{
                    margin: 0, // Removed bottom margin
                    fontSize: '18px',
                    fontWeight: '600',
                    color: 'var(--color2)' // Changed from --color1
                  }}>
                    {module.questionModule} <span style={{
                      fontSize: '14px', // Smaller than module name
                      color: 'black', // Black font
                      fontWeight: '400' // Normal weight
                    }}>({module.questions.length} question{module.questions.length !== 1 ? 's' : ''})</span>
                  </h4>
                </div>
                <div style={{
                  fontSize: '18px',
                  color: 'var(--color1)'
                }}>
                  ▶
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SharedQuestionsForOneCourse;
